import * as React from 'react';

import { Alert, Autocomplete, Box, Skeleton, Stack, TextField } from '@mui/material';
import FormSection from 'components/ReportRequestV2/FormSection';
import OptimizationRunCard from 'components/ReportRequestV2/OptimizationRunCard';

import { StepProps } from './types';
import {
  OptimizationRun,
  useOptimizationRuns,
  useOptimizationVersions,
} from '../hooks/useOptimizationRuns';
import { MixAndMatchSelection } from '../model/types';

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

interface RunProps {
  run: OptimizationRun;
  selection?: MixAndMatchSelection;
  error?: string;
  onChange: (next: MixAndMatchSelection | null) => void;
}

const RunCard: React.FC<RunProps> = ({ run, selection, error, onChange }) => {
  const { versions, loading, error: loadError } = useOptimizationVersions(run.reportId);
  const selected = !!selection;
  const subtitle = [
    formatDate(run.startedAt),
    run.passedQa ? 'QA passed' : 'Not QA passed',
    run.isMixAndMatch ? 'Mix & Match' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <OptimizationRunCard
      reportId={run.reportId}
      title={run.projectName}
      subtitle={subtitle}
      selected={selected}
      onToggle={() =>
        onChange(
          selected
            ? null
            : { reportId: run.reportId, artifactPath: run.artifactPath, label: run.reportId, versions: [] },
        )
      }
      versions={versions}
      selectedVersions={selection?.versions ?? []}
      onToggleVersion={(v) => {
        const base = selection ?? {
          reportId: run.reportId,
          artifactPath: run.artifactPath,
          label: run.reportId,
          versions: [],
        };
        const next = base.versions.includes(v)
          ? base.versions.filter((x) => x !== v)
          : [...base.versions, v].sort((a, b) => Number(a) - Number(b));
        onChange({ ...base, versions: next });
      }}
      loadingVersions={loading}
      error={loadError ? `Versions failed to load: ${loadError.message}` : error}
    />
  );
};

const MixAndMatchStep: React.FC<StepProps> = ({ state, dispatch, errors }) => {
  const target = state.optimization.targetProduct;
  const { runs, runCounts, loading, error } = useOptimizationRuns(state.parent, target);
  const products = React.useMemo(
    () =>
      [...(state.inherited?.competitiveSetProducts ?? [])].sort(
        (a, b) => (runCounts[b] ?? 0) - (runCounts[a] ?? 0),
      ),
    [state.inherited, runCounts],
  );
  const runLabel = (p: string) => {
    const n = runCounts[p] ?? 0;
    return n === 0 ? 'No runs' : `${n} run${n === 1 ? '' : 's'}`;
  };

  const update = (reportId: string, next: MixAndMatchSelection | null) => {
    const exists = state.mixAndMatch.some((m) => m.reportId === reportId);
    let value: MixAndMatchSelection[];
    if (!next) value = state.mixAndMatch.filter((m) => m.reportId !== reportId);
    else if (exists) value = state.mixAndMatch.map((m) => (m.reportId === reportId ? next : m));
    else value = [...state.mixAndMatch, next];
    dispatch({ type: 'set', key: 'mixAndMatch', value });
  };

  return (
    <>
      <FormSection title="Target" description="Only optimization runs of this product can be combined.">
        <Box sx={{ maxWidth: 560 }}>
          <Autocomplete
            options={products}
            value={target}
            loading={loading}
            getOptionDisabled={(p) => !runCounts[p] && p !== target}
            renderOption={(props, p) => (
              <li {...props} key={p}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                  <span>{p}</span>
                  <Box component="span" sx={{ color: 'text.secondary', typography: 'caption' }}>
                    {runLabel(p)}
                  </Box>
                </Box>
              </li>
            )}
            onChange={(_, value) =>
              dispatch({ type: 'patchOptimization', patch: { targetProduct: value } })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Product"
                required
                error={!!errors['optimization.targetProduct']}
                helperText={errors['optimization.targetProduct']}
              />
            )}
          />
        </Box>
      </FormSection>

      {target && (
        <FormSection
          title="Optimization runs"
          description="Pick the runs to combine and the versions to take from each."
        >
          <Stack spacing={2}>
            {errors.mixAndMatch && <Alert severity="error">{errors.mixAndMatch}</Alert>}
            {error && <Alert severity="error">Optimization runs failed to load: {error.message}</Alert>}
            {loading && <Skeleton variant="rounded" height={96} />}
            {!loading && !error && runs.length === 0 && (
              <Alert severity="info">This product has no optimization runs on the selected parent.</Alert>
            )}
            {runs.map((run) => (
              <RunCard
                key={run.reportId}
                run={run}
                selection={state.mixAndMatch.find((m) => m.reportId === run.reportId)}
                error={errors[`mixAndMatch.${run.reportId}`]}
                onChange={(next) => update(run.reportId, next)}
              />
            ))}
          </Stack>
        </FormSection>
      )}
    </>
  );
};

export default MixAndMatchStep;
